import { useEffect, useState } from "react";
import { Button, Card, Text, makeStyles, mergeClasses, tokens } from "@fluentui/react-components";
import { Checkmark16Filled } from "@fluentui/react-icons";

/** Chrome/Edge/Android's install prompt, caught early in index.html. */
interface InstallPrompt extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}
declare global {
  interface Window {
    __installPrompt?: InstallPrompt;
  }
}

type Device = "ios" | "android" | "mac" | "desktop";
function device(): Device {
  const ua = navigator.userAgent;
  if (/iPad|iPhone|iPod/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)) return "ios";
  if (/Android/.test(ua)) return "android";
  if (/Mac/.test(navigator.platform || ua)) return "mac";
  return "desktop";
}
const standalone = () => window.matchMedia?.("(display-mode: standalone)").matches || (navigator as { standalone?: boolean }).standalone === true;

/** Browsers don't let a page add a bookmark itself, so this is the exact step for their device. */
function steps(d: Device): string[] {
  switch (d) {
    case "ios":
      return ["Tap the Share button (the square with an arrow) in Safari's toolbar.", "Choose “Add to Home Screen” — or “Add Bookmark”."];
    case "android":
      return ["Tap the ⋮ menu at the top right of your browser.", "Choose “Add to Home screen” — or tap ☆ to bookmark it."];
    case "mac":
      return ["Press ⌘ + D to bookmark this page."];
    default:
      return ["Press Ctrl + D to bookmark this page."];
  }
}

const useStyles = makeStyles({
  card: { padding: "14px 16px", display: "flex", flexDirection: "row", gap: "12px", alignItems: "flex-start" },
  avatar: { width: "32px", height: "32px", flexShrink: 0, borderRadius: "50%", backgroundColor: tokens.colorNeutralBackground1, boxShadow: tokens.shadow4, padding: "3px", boxSizing: "border-box" },
  body: { display: "flex", flexDirection: "column", gap: "8px", minWidth: 0, flex: 1 },
  actions: { display: "flex", gap: "8px", flexWrap: "wrap" },
  steps: { margin: 0, paddingLeft: "20px", display: "flex", flexDirection: "column", gap: "4px" },
  done: { color: tokens.colorPaletteGreenForeground1, display: "inline-flex", alignItems: "center", gap: "6px" },
});

type Phase = "ask" | "steps" | "added" | "hidden";
const keyFor = (token: string) => `rackup.savedPage.${token}`;

/**
 * Asks the client whether they'd like to save their page. "Yes" opens the browser's own
 * "add to home screen" prompt where there is one; otherwise it shows the steps for their device.
 * Remembered per page in this browser. `reopen` lets a link elsewhere on the page ask again.
 */
export function SavePage({ token, trainer, reopen }: { token: string; trainer: string; reopen: number }) {
  const s = useStyles();
  const [phase, setPhase] = useState<Phase>(() => {
    if (standalone()) return "hidden";
    try {
      return localStorage.getItem(keyFor(token)) ? "hidden" : "ask";
    } catch {
      return "ask";
    }
  });
  const remember = (v: string) => {
    try {
      localStorage.setItem(keyFor(token), v);
    } catch {
      /* private mode: ask again next time */
    }
  };
  useEffect(() => {
    if (reopen > 0 && !standalone()) setPhase("ask");
  }, [reopen]);
  useEffect(() => {
    const onInstalled = () => {
      remember("installed");
      setPhase("added");
    };
    window.addEventListener("appinstalled", onInstalled);
    return () => window.removeEventListener("appinstalled", onInstalled);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (phase === "hidden") return null;

  const yes = async () => {
    const p = window.__installPrompt;
    if (p) {
      window.__installPrompt = undefined; // each prompt can only be shown once
      await p.prompt();
      const { outcome } = await p.userChoice;
      if (outcome === "accepted") {
        remember("installed");
        return setPhase("added");
      }
    }
    setPhase("steps");
  };
  const no = () => {
    remember("declined");
    setPhase("hidden");
  };
  const done = () => {
    remember("bookmarked");
    setPhase("hidden");
  };

  return (
    <Card className={s.card} role="region" aria-label="Save this page">
      <img src="/icon-192.png" alt="RackUp" className={s.avatar} />
      <div className={s.body}>
        {phase === "ask" && (
          <>
            <Text>
              Would you like to bookmark this page? It's yours for changing your times with {trainer} — saving it means you won't need to find the link again.
            </Text>
            <div className={s.actions}>
              <Button appearance="primary" size="small" onClick={() => void yes()}>Yes, save it</Button>
              <Button size="small" onClick={no}>No thanks</Button>
            </div>
          </>
        )}
        {phase === "steps" && (
          <>
            <Text>Your browser doesn't let websites add bookmarks themselves, so here's the quickest way:</Text>
            <ol className={s.steps}>
              {steps(device()).map((t) => (
                <li key={t}>
                  <Text>{t}</Text>
                </li>
              ))}
            </ol>
            <div className={s.actions}>
              <Button appearance="primary" size="small" onClick={done}>Done</Button>
              <Button size="small" appearance="subtle" onClick={no}>Not now</Button>
            </div>
          </>
        )}
        {phase === "added" && (
          <div className={mergeClasses(s.actions)}>
            <Text className={s.done}>
              <Checkmark16Filled /> Saved — RackUp is on your home screen.
            </Text>
            <Button size="small" appearance="subtle" onClick={() => setPhase("hidden")}>Close</Button>
          </div>
        )}
        <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
          This page is private to you — please don't share the link.
        </Text>
      </div>
    </Card>
  );
}
